import React from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { cloneDeep } from 'lodash';
import { IAmendmentGeneralData } from '../line-amendment';
import { IAmendmentCostCharges, IAmendmentPageState } from '..';
import { validateCostWidgetField } from './validation';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import { useAppSelector } from '@redux/hooks';

interface ICostsAndCharges {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled?: boolean;
}

const CostsAndCharges: React.FC<ICostsAndCharges> = ({
  generalData,
  pageState,
  onPageStateUpdate,
  isDisabled,
}) => {
  const { businessUser_EditableLineChargesIDs } = useAppSelector(
    (state) => state.user.info
  );
  const localIsDisabled =
    isDisabled ||
    !businessUser_EditableLineChargesIDs.externalCode.includes(
      generalData.line.externalCode
    );
  const values = pageState.values.costs;
  const errors = pageState.errors.costs;
  const touched = pageState.touched.costs;

  const onFieldBlur = (fieldName: keyof IAmendmentCostCharges) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.costs[fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IAmendmentCostCharges,
    value: IAmendmentCostCharges[keyof IAmendmentCostCharges],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);

    (newPageState.values.costs as any)[fieldName] = value;
    newPageState.errors.costs[fieldName] = validateCostWidgetField(
      fieldName,
      value,
      newPageState.values.costs,
      generalData.line.externalCode
    );

    if (fieldName === 'tpaFeesType') {
      newPageState.values.costs.tpaFeesPercentage = null;
      newPageState.values.costs.tpaFeesAmount = null;

      newPageState.errors.costs.tpaFeesPercentage = '';
      newPageState.errors.costs.tpaFeesAmount = '';

      newPageState.touched.costs.tpaFeesPercentage = false;
      newPageState.touched.costs.tpaFeesAmount = false;
    }

    if (touched) {
      newPageState.touched.costs[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Costs and Charges" isFieldsGrid fieldsPerRow={3}>
        <CurrencyFormField
          key="amendmentCost"
          name="amendmentCost"
          title="Amendment Cost*"
          placeholder="Policy Cost"
          value={values.amendmentCost}
          error={touched.amendmentCost ? errors.amendmentCost : ''}
          onBlur={() => onFieldBlur('amendmentCost')}
          onChange={(v) => onFieldUpdate('amendmentCost', v.target.value)}
          disabled={localIsDisabled}
          material
          allowNegative
          currencySymbol={generalData.currency.Symbol}
        />

        <PercentageFormField
          key="chargesPercentage"
          name="chargesPercentage"
          title="Charges Percentage Variance*"
          placeholder="Charges Percentage Variance"
          value={values.chargesPercentage}
          error={touched.chargesPercentage ? errors.chargesPercentage : ''}
          onBlur={() => onFieldBlur('chargesPercentage')}
          onChange={(v) => onFieldUpdate('chargesPercentage', v.target.value)}
          disabled={localIsDisabled}
          material
        />
        {values.tpaFeesType === 'PERCENTAGE' ? (
          <PercentageFormField
            key="tpaFeesPercentage"
            name="tpaFeesPercentage"
            title="TPA Fees Percentage Variance*"
            placeholder="TPA Fees Percentage Variance"
            value={values.tpaFeesPercentage}
            error={touched.tpaFeesPercentage ? errors.tpaFeesPercentage : ''}
            onBlur={() => onFieldBlur('tpaFeesPercentage')}
            onChange={(v) => onFieldUpdate('tpaFeesPercentage', v.target.value)}
            disabled={localIsDisabled}
            material
          />
        ) : (
          <CurrencyFormField
            key="tpaFeesAmount"
            name="tpaFeesAmount"
            title="TPA Fees Variance*"
            placeholder="TPA Fees Variance"
            value={values.tpaFeesAmount}
            error={touched.tpaFeesAmount ? errors.tpaFeesAmount : ''}
            onBlur={() => onFieldBlur('tpaFeesAmount')}
            onChange={(v) => onFieldUpdate('tpaFeesAmount', v.target.value)}
            disabled={localIsDisabled}
            material
            allowNegative
            currencySymbol={generalData.currency.Symbol}
          />
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default CostsAndCharges;
